import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CreditCard,
  Building2,
  Wallet,
  Plus,
  Printer,
  ChevronLeft,
  ChevronRight,
  Save,
  Trash2,
  Calculator,
  Upload,
  FileSpreadsheet,
} from "lucide-react";
import { format, getDaysInMonth, startOfMonth } from "date-fns";
import { it } from "date-fns/locale";
import * as XLSX from "xlsx";
import api from "@/lib/api";

const MONTHS = [
  { value: 1, label: "Gennaio" },
  { value: 2, label: "Febbraio" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Aprile" },
  { value: 5, label: "Maggio" },
  { value: 6, label: "Giugno" },
  { value: 7, label: "Luglio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Settembre" },
  { value: 10, label: "Ottobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Dicembre" },
];

const YEARS = [2024, 2025, 2026, 2027];

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(value || 0);
};

// Helper to create initial empty day
const createEmptyDay = (date) => ({
  date,
  widiba: 0,
  paypal: 0,
  unicredit: 0,
  total: 0,
});

// Component for Totals Card
const TotalsCard = ({ totals }) => (
  <Card className="bg-white border-gray-200 shadow-sm">
    <CardHeader className="pb-2">
      <CardTitle className="text-lg text-gray-900">Totali Mensili</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-emerald-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-emerald-600" />
            <span className="text-sm text-emerald-700">Widiba</span>
          </div>
          <p className="text-xl font-bold text-emerald-800">
            {formatCurrency(totals.widiba)}
          </p>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700">PayPal</span>
          </div>
          <p className="text-xl font-bold text-blue-800">
            {formatCurrency(totals.paypal)}
          </p>
        </div>
        <div className="p-4 bg-amber-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700">Unicredit</span>
          </div>
          <p className="text-xl font-bold text-amber-800">
            {formatCurrency(totals.unicredit)}
          </p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Calculator className="w-4 h-4 text-purple-600" />
            <span className="text-sm text-purple-700">Totale</span>
          </div>
          <p className="text-xl font-bold text-purple-800">
            {formatCurrency(totals.grand_total)}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Entry Row Component
const EntryRow = ({ entry, isEditing, editValues, onStartEdit, onSave, onCancel, onDelete, onEditChange }) => {
  const dayDate = new Date(entry.date);
  const dayOfWeek = format(dayDate, "EEEE", { locale: it });
  const isWeekend = dayOfWeek === "sabato" || dayOfWeek === "domenica";

  if (isEditing) {
    return (
      <TableRow className="bg-amber-50">
        <TableCell className="font-medium">
          {format(dayDate, "d")} - {dayOfWeek}
        </TableCell>
        <TableCell>
          <Input
            type="number"
            step="0.01"
            value={editValues.widiba}
            onChange={(e) => onEditChange("widiba", parseFloat(e.target.value) || 0)}
            className="w-24 text-right"
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            step="0.01"
            value={editValues.paypal}
            onChange={(e) => onEditChange("paypal", parseFloat(e.target.value) || 0)}
            className="w-24 text-right"
          />
        </TableCell>
        <TableCell>
          <Input
            type="number"
            step="0.01"
            value={editValues.unicredit}
            onChange={(e) => onEditChange("unicredit", parseFloat(e.target.value) || 0)}
            className="w-24 text-right"
          />
        </TableCell>
        <TableCell className="text-right font-bold">
          {formatCurrency(editValues.widiba + editValues.paypal + editValues.unicredit)}
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button size="sm" onClick={onSave} className="bg-green-600 hover:bg-green-700">
              <Save className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow className={isWeekend ? "bg-gray-50" : ""}>
      <TableCell className="font-medium">
        {format(dayDate, "d")} - {dayOfWeek}
      </TableCell>
      <TableCell className={entry.widiba > 0 ? "text-emerald-600 font-medium" : "text-gray-400"}>
        {formatCurrency(entry.widiba)}
      </TableCell>
      <TableCell className={entry.paypal > 0 ? "text-blue-600 font-medium" : "text-gray-400"}>
        {formatCurrency(entry.paypal)}
      </TableCell>
      <TableCell className={entry.unicredit > 0 ? "text-amber-600 font-medium" : "text-gray-400"}>
        {formatCurrency(entry.unicredit)}
      </TableCell>
      <TableCell className="text-right font-bold">
        {formatCurrency(entry.total)}
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onStartEdit(entry)}
          className="hover:bg-gray-100"
        >
          <Plus className="w-3 h-3 mr-1" />
          Modifica
        </Button>
      </TableCell>
    </TableRow>
  );
};

const Corrispettivi = () => {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ widiba: 0, paypal: 0, unicredit: 0, grand_total: 0 });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [editingDate, setEditingDate] = useState(null);
  const [editValues, setEditValues] = useState({ widiba: 0, paypal: 0, unicredit: 0 });
  const printRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/corrispettivi", {
        params: { month: selectedMonth, year: selectedYear },
      });
      
      const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
      const fullEntries = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const existing = res.data.entries?.find((e) => e.date === dateStr);
        
        if (existing) {
          fullEntries.push({
            ...existing,
            total: (existing.widiba || 0) + (existing.paypal || 0) + (existing.unicredit || 0),
          });
        } else {
          fullEntries.push(createEmptyDay(dateStr));
        }
      }
      
      setEntries(fullEntries);
      setTotals(res.data.totals || { widiba: 0, paypal: 0, unicredit: 0, grand_total: 0 });
    } catch (error) {
      console.error("Error loading corrispettivi:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle edit
  const startEdit = useCallback((entry) => {
    setEditingDate(entry.date);
    setEditValues({
      widiba: entry.widiba || 0,
      paypal: entry.paypal || 0,
      unicredit: entry.unicredit || 0,
    });
  }, []);

  const handleEditChange = useCallback((field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveEdit = useCallback(async () => {
    try {
      await api.post("/corrispettivi", {
        date: editingDate,
        ...editValues,
      });
      toast.success("Salvato con successo");
      setEditingDate(null);
      loadData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  }, [editingDate, editValues, loadData]);

  const cancelEdit = useCallback(() => {
    setEditingDate(null);
    setEditValues({ widiba: 0, paypal: 0, unicredit: 0 });
  }, []);

  // Month navigation
  const prevMonth = useCallback(() => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonth((m) => m - 1);
    }
  }, [selectedMonth]);

  const nextMonth = useCallback(() => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonth((m) => m + 1);
    }
  }, [selectedMonth]);

  // Print
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Excel import handler
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    toast.info("Lettura file Excel...");

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      const monthsConfig = [
        { month: "01", widiba: "B", paypal: "G", unicredit: "L" },
        { month: "02", widiba: "C", paypal: "H", unicredit: "M" },
        { month: "03", widiba: "D", paypal: "I", unicredit: "N" },
        { month: "04", widiba: "E", paypal: "J", unicredit: "O" },
      ];

      const allEntries = [];

      for (const config of monthsConfig) {
        for (let row = 2; row <= 32; row++) {
          const dayCell = worksheet[`A${row}`];
          if (!dayCell) continue;

          let day = dayCell.v;
          if (typeof day === "number") {
            day = Math.floor(day);
          } else {
            continue;
          }

          const widibaCell = worksheet[`${config.widiba}${row}`];
          const paypalCell = worksheet[`${config.paypal}${row}`];
          const unicreditCell = worksheet[`${config.unicredit}${row}`];

          const widiba = widibaCell?.v || 0;
          const paypal = paypalCell?.v || 0;
          const unicredit = unicreditCell?.v || 0;

          if (widiba > 0 || paypal > 0 || unicredit > 0) {
            const dateStr = `${selectedYear}-${config.month}-${String(day).padStart(2, "0")}`;
            allEntries.push({ date: dateStr, widiba, paypal, unicredit });
          }
        }
      }

      if (allEntries.length > 0) {
        await api.post("/corrispettivi/bulk", { entries: allEntries });
        toast.success(`Importati ${allEntries.length} record`);
        loadData();
      } else {
        toast.warning("Nessun dato trovato nel file");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Errore nell'importazione");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [selectedYear, loadData]);

  // Memoized month label
  const monthLabel = useMemo(() => {
    return MONTHS.find((m) => m.value === selectedMonth)?.label || "";
  }, [selectedMonth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500 animate-pulse">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="corrispettivi-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">
            Corrispettivi
          </h1>
          <p className="text-gray-500">Registro giornaliero incassi per conto bancario</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? "Importo..." : "Importa Excel"}
          </Button>
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Stampa
          </Button>
        </div>
      </div>

      {/* Month Selector */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={prevMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-4">
              <Select
                value={String(selectedMonth)}
                onValueChange={(v) => setSelectedMonth(parseInt(v))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={String(selectedYear)}
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" onClick={nextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      <TotalsCard totals={totals} />

      {/* Data Table */}
      <Card className="bg-white border-gray-200 shadow-sm" ref={printRef}>
        <CardHeader>
          <CardTitle className="text-lg text-gray-900">
            {monthLabel} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Giorno</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Wallet className="w-4 h-4 text-emerald-600" />
                      Widiba
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      PayPal
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-4 h-4 text-amber-600" />
                      Unicredit
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead className="w-24">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <EntryRow
                    key={entry.date}
                    entry={entry}
                    isEditing={editingDate === entry.date}
                    editValues={editValues}
                    onStartEdit={startEdit}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    onDelete={() => {}}
                    onEditChange={handleEditChange}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Corrispettivi;
